import {
	forwardRef,
	useContext,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import SeriesItemDataCard from './subcomponents/SeriesItemDataCard';
import SeriesItemImage from './subcomponents/SeriesItemImage';
import SaveButton from '../../save/SaveButton';
import ErrorBoundary from '../../../../../_helpers/ErrorBoundary';
import { getAriaLabel } from './_helpers/aria-label';
import { SeriesItemCardProps, SaveBtnPosition } from './SeriesItemCardProps';
import { SaveButtonProps } from '../../save/SaveButtonProps';
import { useFocus } from '../../../../../_helpers/hooks/useFocus';
import PaginationContext from '../../pagination/context';
import classNames from 'classnames';

import './SeriesItemCard.scss';
import { sendSeriesItemCardDataLinkEvent } from '../../_helpers/dataLinkEventHelper.ts';

const SeriesItemCard = forwardRef<HTMLAnchorElement, SeriesItemCardProps>(
	(
		{
			itemData,
			componentData,
			cardConfig,
			headingLevel,
			className,
			focused,
			hidden,
			isComponentFrameVisible = true,
			onSaveButtonClick,
		},
		ref
	) => {
		const cardContainerRef = useRef<HTMLDivElement>(null);
		const cardLinkRef = useRef<HTMLAnchorElement>(null);
		const [hovered, setHovered] = useState<boolean>(false);
		const [focusVisible, setFocusVisible] = useState<boolean>(false);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		useFocus<HTMLAnchorElement>(focused, isComponentFrameVisible, cardLinkRef);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		useImperativeHandle(ref, () => cardLinkRef.current);

		// Optional pagination context for providing a11y label.
		const pagination = useContext(PaginationContext);

		const pageAria =
			pagination && focused && isComponentFrameVisible
				? pagination.label.replace('%s', '' + pagination.selectedPage) + '! '
				: '';

		const onBlur = (event: React.FocusEvent) => {
			if (
				event.relatedTarget &&
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				cardContainerRef.current.contains(event.relatedTarget)
			) {
				return;
			}
			setFocusVisible(false);
		};

		const onCardLinkClick = () => {
			sendSeriesItemCardDataLinkEvent(
				itemData.dataLinkEvent,
				itemData.itemType,
				itemData.url
			);
		};

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const saveButtonProps: SaveButtonProps = itemData.savable
			? {
					className: cardConfig.saveBtnPosition,
					variant: cardConfig.saveBtnVariant,
					isSaved: itemData.saved,
					saveConfig: componentData.saveConfig,
					item: itemData,
					onClick: onSaveButtonClick,
					buttonState: itemData.saveButtonState,
					parentIsHovered: hovered,
					parentIsFocusVisible: focusVisible,
				}
			: undefined;

		return (
			<div
				className={classNames('blk-ac-item-card', className, {
					hidden: hidden,
				})}
				ref={cardContainerRef}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onKeyUp={() => setFocusVisible(true)}
				onBlur={onBlur}
			>
				<a
					className="blk-ac-item-card-link"
					data-final-url
					href={itemData.url}
					target={itemData.target}
					onClick={onCardLinkClick}
					ref={cardLinkRef}
					aria-label={
						pageAria +
						getAriaLabel(
							itemData,
							componentData,
							cardConfig.descriptionDisabled
						)
					}
				/>
				{cardConfig.displayImage && (
					<SeriesItemImage
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						imagePath={itemData.imagePath}
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						imageFallbackList={cardConfig.imageFallbackList}
						saveUrl={itemData.saveUrl || itemData.url}
						hidden={hidden}
					/>
				)}

				<SeriesItemDataCard
					{...{
						itemData,
						componentData,
						cardConfig,
						headingLevel,
					}}
				>
					{itemData.savable &&
						cardConfig.saveBtnPosition ===
							SaveBtnPosition.IN_LINE_WITH_ARTICLE_DETAIL && (
							<ErrorBoundary>
								<SaveButton {...saveButtonProps} />
							</ErrorBoundary>
						)}
				</SeriesItemDataCard>

				{itemData.savable &&
					cardConfig.saveBtnPosition ===
						SaveBtnPosition.IN_THE_LOWER_RIGHT_CORNER_OF_THE_CARD && (
						<ErrorBoundary>
							<SaveButton {...saveButtonProps} />
						</ErrorBoundary>
					)}
			</div>
		);
	}
);

SeriesItemCard.displayName = 'SeriesItemCard';

export default SeriesItemCard;
